import React, { useState } from 'react';
import { theme } from '../styles/theme';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Card } from '../components/Card';
import { Button } from '../components/Button';
import { Select } from '../components/Select';
import { Textarea } from '../components/Textarea';

export const AgencySettingsPage = () => {
  // Global Preferences
  const [autoResponse, setAutoResponse] = useState('enabled');
  const [notifyOnQuery, setNotifyOnQuery] = useState('all');
  const [requireExclusive, setRequireExclusive] = useState('no');
  const [allowSimultaneous, setAllowSimultaneous] = useState('yes');

  // Submission Rules
  const [maxQueryLength, setMaxQueryLength] = useState('500');
  const [maxSynopsisLength, setMaxSynopsisLength] = useState('1000');
  const [maxFirstPages, setMaxFirstPages] = useState('10');
  const [requireSynopsis, setRequireSynopsis] = useState('yes');
  const [requireAuthorBio, setRequireAuthorBio] = useState('yes');

  // Auto-Response Templates
  const [queryReceivedTemplate, setQueryReceivedTemplate] = useState(
    `Dear [Author Name],\n\nThank you for your query to Sterling Literary Agency. We have received your submission for [TITLE] and will review it as soon as possible.\n\nOur current response time is 6-8 weeks. If you have not heard from us after 8 weeks, please feel free to follow up.\n\nBest regards,\nSterling Literary Agency`
  );

  const [fullRequestTemplate, setFullRequestTemplate] = useState(
    `Dear [Author Name],\n\nThank you for your query. [Agent Name] is interested in reading the full manuscript of [TITLE].\n\nPlease submit the complete manuscript as a Word document (.doc or .docx) to [Agent Email] at your earliest convenience. We aim to respond within [Timeframe].\n\nBest regards,\nSterling Literary Agency`
  );

  const [partialRequestTemplate, setPartialRequestTemplate] = useState(
    `Dear [Author Name],\n\nThank you for your query. [Agent Name] would like to read more of [TITLE].\n\nPlease submit the first [Pages] pages of your manuscript as a Word document (.doc or .docx) to [Agent Email] at your earliest convenience. We aim to respond within [Timeframe].\n\nBest regards,\nSterling Literary Agency`
  );

  // Branding
  const [primaryColor, setPrimaryColor] = useState('#2563eb');
  const [fontFamily, setFontFamily] = useState('inter');
  const [emailFooter, setEmailFooter] = useState(
    `Sterling Literary Agency\nNew York, NY\nwww.sterlingliterary.com\n\n© 2024 Sterling Literary Agency. All rights reserved.`
  );

  const handleSave = () => {
    console.log('Saving agency settings:', {
      autoResponse,
      notifyOnQuery,
      requireExclusive,
      allowSimultaneous,
      maxQueryLength,
      maxSynopsisLength,
      maxFirstPages,
      requireSynopsis,
      requireAuthorBio,
      queryReceivedTemplate,
      fullRequestTemplate,
      partialRequestTemplate,
      primaryColor,
      fontFamily,
      emailFooter,
    });
    window.alert('Agency settings saved successfully!');
  };

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: theme.colors.background,
      fontFamily: theme.fonts.primary,
    }}>
      <Header />

      <main style={{
        maxWidth: '900px',
        margin: '0 auto',
        padding: '60px 40px',
      }}>
        {/* Header */}
        <div style={{ marginBottom: '32px' }}>
          <h1 style={{
            fontSize: '40px',
            fontWeight: '400',
            marginBottom: '8px',
            color: theme.colors.textPrimary,
          }}>
            Agency Settings
          </h1>
          <p style={{
            fontSize: '18px',
            color: theme.colors.textSecondary,
          }}>
            Configure global preferences and submission rules for your agency
          </p>
        </div>

        {/* Global Preferences */}
        <Card style={{ marginBottom: '24px' }}>
          <h3 style={{
            fontSize: '18px',
            fontWeight: '500',
            color: theme.colors.textPrimary,
            marginBottom: '20px',
          }}>
            Global Preferences
          </h3>

          <Select
            label="Auto-Response on Query Receipt"
            value={autoResponse}
            onChange={(e) => setAutoResponse(e.target.value)}
            options={[
              { value: 'enabled', label: 'Enabled - Send automatic confirmation' },
              { value: 'disabled', label: 'Disabled - No automatic response' },
            ]}
            helperText="Automatically send confirmation email when queries are received"
            style={{ marginBottom: '16px' }}
          />

          <Select
            label="Notify Agents on New Query"
            value={notifyOnQuery}
            onChange={(e) => setNotifyOnQuery(e.target.value)}
            options={[
              { value: 'all', label: 'All queries - Notify immediately' },
              { value: 'matched', label: 'Genre-matched only - Only if genre matches' },
              { value: 'none', label: 'None - No notifications' },
            ]}
            helperText="When to send email notifications to agents about new queries"
            style={{ marginBottom: '16px' }}
          />

          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '16px',
          }}>
            <Select
              label="Require Exclusive Submissions"
              value={requireExclusive}
              onChange={(e) => setRequireExclusive(e.target.value)}
              options={[
                { value: 'yes', label: 'Yes - Required' },
                { value: 'no', label: 'No - Not required' },
              ]}
            />

            <Select
              label="Allow Simultaneous Submissions"
              value={allowSimultaneous}
              onChange={(e) => setAllowSimultaneous(e.target.value)}
              options={[
                { value: 'yes', label: 'Yes - Allowed' },
                { value: 'no', label: 'No - Not allowed' },
              ]}
            />
          </div>
        </Card>

        {/* Submission Rules */}
        <Card style={{ marginBottom: '24px' }}>
          <h3 style={{
            fontSize: '18px',
            fontWeight: '500',
            color: theme.colors.textPrimary,
            marginBottom: '20px',
          }}>
            Submission Rules
          </h3>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '16px',
            marginBottom: '16px',
          }}>
            <Select
              label="Max Query Letter Length"
              value={maxQueryLength}
              onChange={(e) => setMaxQueryLength(e.target.value)}
              options={[
                { value: '300', label: '300 words' },
                { value: '500', label: '500 words' },
                { value: '750', label: '750 words' },
                { value: 'unlimited', label: 'Unlimited' },
              ]}
            />

            <Select
              label="Max Synopsis Length"
              value={maxSynopsisLength}
              onChange={(e) => setMaxSynopsisLength(e.target.value)}
              options={[
                { value: '500', label: '500 words' },
                { value: '1000', label: '1000 words' },
                { value: '1500', label: '1500 words' },
                { value: 'unlimited', label: 'Unlimited' },
              ]}
            />

            <Select
              label="Max First Pages"
              value={maxFirstPages}
              onChange={(e) => setMaxFirstPages(e.target.value)}
              options={[
                { value: '5', label: '5 pages' },
                { value: '10', label: '10 pages' },
                { value: '25', label: '25 pages' },
                { value: '50', label: '50 pages' },
              ]}
            />
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '16px',
          }}>
            <Select
              label="Require Synopsis"
              value={requireSynopsis}
              onChange={(e) => setRequireSynopsis(e.target.value)}
              options={[
                { value: 'yes', label: 'Yes - Required' },
                { value: 'no', label: 'No - Optional' },
              ]}
            />

            <Select
              label="Require Author Bio"
              value={requireAuthorBio}
              onChange={(e) => setRequireAuthorBio(e.target.value)}
              options={[
                { value: 'yes', label: 'Yes - Required' },
                { value: 'no', label: 'No - Optional' },
              ]}
            />
          </div>
        </Card>

        {/* Auto-Response Templates */}
        <Card style={{ marginBottom: '24px' }}>
          <h3 style={{
            fontSize: '18px',
            fontWeight: '500',
            color: theme.colors.textPrimary,
            marginBottom: '8px',
          }}>
            Auto-Response Templates
          </h3>
          <p style={{
            fontSize: '14px',
            color: theme.colors.textSecondary,
            marginBottom: '20px',
          }}>
            Use placeholders: [Author Name], [TITLE], [Agent Name], [Agent Email], [Timeframe], [Pages]
          </p>

          <Textarea
            label="Query Received Confirmation"
            placeholder="Template for automatic confirmation when query is received..."
            value={queryReceivedTemplate}
            onChange={(e) => setQueryReceivedTemplate(e.target.value)}
            rows={8}
            helperText="Sent automatically when a query is submitted (if auto-response is enabled)"
            style={{ marginBottom: '16px' }}
          />

          <Textarea
            label="Full Manuscript Request"
            placeholder="Template for full manuscript requests..."
            value={fullRequestTemplate}
            onChange={(e) => setFullRequestTemplate(e.target.value)}
            rows={8}
            helperText="Default template when agents request full manuscripts"
            style={{ marginBottom: '16px' }}
          />

          <Textarea
            label="Partial Manuscript Request"
            placeholder="Template for partial manuscript requests..."
            value={partialRequestTemplate}
            onChange={(e) => setPartialRequestTemplate(e.target.value)}
            rows={8}
            helperText="Default template when agents request partial manuscripts"
          />
        </Card>

        {/* Branding Options */}
        <Card style={{ marginBottom: '24px' }}>
          <h3 style={{
            fontSize: '18px',
            fontWeight: '500',
            color: theme.colors.textPrimary,
            marginBottom: '20px',
          }}>
            Branding Options
          </h3>

          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 2fr',
            gap: '16px',
            marginBottom: '16px',
            alignItems: 'flex-start',
          }}>
            <div>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                color: theme.colors.textPrimary,
                marginBottom: '8px',
              }}>
                Primary Brand Color
              </label>
              <input
                type="color"
                value={primaryColor}
                onChange={(e) => setPrimaryColor(e.target.value)}
                style={{
                  width: '100%',
                  height: '48px',
                  border: `1px solid ${theme.colors.border}`,
                  borderRadius: theme.borderRadius.medium,
                  cursor: 'pointer',
                }}
              />
              <div style={{
                marginTop: '8px',
                fontSize: '13px',
                color: theme.colors.textSecondary,
              }}>
                {primaryColor}
              </div>
            </div>

            <Select
              label="Email Font Family"
              value={fontFamily}
              onChange={(e) => setFontFamily(e.target.value)}
              options={[
                { value: 'inter', label: 'Inter (Modern, Clean)' },
                { value: 'georgia', label: 'Georgia (Classic, Serif)' },
                { value: 'arial', label: 'Arial (Simple, Sans-Serif)' },
                { value: 'times', label: 'Times New Roman (Traditional)' },
              ]}
              helperText="Font used in automated emails and communications"
            />
          </div>

          <Textarea
            label="Email Footer"
            placeholder="Agency contact information and legal text for email footers..."
            value={emailFooter}
            onChange={(e) => setEmailFooter(e.target.value)}
            rows={5}
            helperText="Appears at the bottom of all automated emails"
          />
        </Card>

        {/* Info Box */}
        <Card style={{
          marginBottom: '32px',
          backgroundColor: theme.colors.primaryLight,
          border: `1px solid ${theme.colors.primary}`,
        }}>
          <div style={{
            display: 'flex',
            gap: '12px',
            alignItems: 'flex-start',
          }}>
            <div style={{ fontSize: '20px' }}>ℹ️</div>
            <div>
              <div style={{
                fontSize: '16px',
                fontWeight: '500',
                color: theme.colors.textPrimary,
                marginBottom: '8px',
              }}>
                About Agency Settings
              </div>
              <ul style={{
                fontSize: '14px',
                color: theme.colors.textSecondary,
                margin: 0,
                paddingLeft: '20px',
                lineHeight: '1.6',
              }}>
                <li>These settings apply to all agents in your agency</li>
                <li>Individual agents can override some settings in their profiles</li>
                <li>Changes to auto-response templates take effect immediately</li>
                <li>Submission rules help ensure consistent quality across queries</li>
              </ul>
            </div>
          </div>
        </Card>

        {/* Action Buttons */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: '16px',
        }}>
          <Button
            variant="ghost"
            onClick={() => window.location.href = '/agency/dashboard'}
          >
            ← Back to Dashboard
          </Button>
          <Button onClick={handleSave}>
            Save Settings
          </Button>
        </div>
      </main>

      <Footer />
    </div>
  );
};
